"use client"

import * as React from "react"
import {useEffect, useState} from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table"

import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from "../table"

import {DataTablePagination} from "./datatablepagination"
import {DataTableToolbar} from "./datatabletoolbar";
import {DataTableViewOptions} from "./datatableviewoptions";
import {LoadingSpinner} from "../customSpinner";
import {cn} from "../lib/utils"
import {Input} from "../input";

export interface ColumnConfig {
    id: string,
    title?: string
    hiddenByDefault?: boolean,
    valueRegexExtractor?: string,
    isFilterable?: boolean
}

interface DataTableProps<TData> {
    columns: ColumnDef<TData>[]
    data: TData[]
    tableTitle?: string
    setTitle?: (title: string) => void
    textFilteringColumnName?: string
    textFilteringPlaceholder?: string
    filters: { filteringColumn: string, title: string, defaultValues?: string[] }[]
    showPagination?: boolean
    isLoading?: boolean
    setFilters?: (filters: any[]) => void
    removeBorder?: boolean
    forcePagination?: boolean
    isEditable?: boolean
    columnConfigs?: ColumnConfig[]
    setColumnConfigs?: React.Dispatch<React.SetStateAction<ColumnConfig[]>>
    emptyStateMessage?: React.ReactNode
    className?: string
    defaultPageSize?: number
    onRowClick?: (row: TData) => void
}


function getVisibility<TData>(columns: ColumnDef<TData>[]): VisibilityState {
    const visibility: VisibilityState = {}
    columns.forEach((column) => {
        // @ts-ignore
        if (column.show !== undefined && column.show === false) {
            visibility[column.id!] = false
        }
    })
    return visibility
}

function combineColumnsWithConfigs<TData>(columns: ColumnDef<TData>[], columnConfigs?: ColumnConfig[]): ColumnDef<TData>[] {
    // Combine the columns with the columnConfigs
    return columns.map((column) => {
        const columnConfig = columnConfigs?.find((config) => config.id === column.id)
        if (columnConfig) {
            return {
                ...column,
                id: columnConfig.id,
                // @ts-ignore
                show: columnConfig.hiddenByDefault !== undefined ? !columnConfig.hiddenByDefault : true,
                meta: {
                    ...column.meta,
                    title: columnConfig.title !== undefined && columnConfig.title != "" ? columnConfig.title! : columnConfig.id,
                    valueRegexExtractor: columnConfig.valueRegexExtractor
                },
            }
        }
        return column
    })
}

export function DataTable<TData>({
                                     columns,
                                     data,
                                     textFilteringColumnName,
                                     textFilteringPlaceholder,
                                     filters,
                                     showPagination = true,
                                     tableTitle,
                                     setTitle,
                                     isLoading = false,
                                     setFilters,
                                     removeBorder = false,
                                     forcePagination = false,
                                     columnConfigs,
                                     setColumnConfigs,
                                     isEditable = false,
                                     emptyStateMessage,
                                     className,
                                     defaultPageSize,
                                     onRowClick
                                 }: DataTableProps<TData>) {
    const [rowSelection, setRowSelection] = React.useState({})
    const [containerWidth, setContainerWidth] = React.useState<number>(0);
    const [containerHeight, setContainerHeight] = React.useState<number>(250);
    const [columnsState, setColumnsState] = useState<ColumnDef<TData>[]>(combineColumnsWithConfigs(columns, columnConfigs))
    useEffect(() => {
        setColumnsState(combineColumnsWithConfigs(columns, columnConfigs))
    }, [columns, columnConfigs]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(getVisibility(columns))

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
                setContainerHeight(entry.contentRect.height);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const visibility = getVisibility(columnsState)
        setColumnVisibility(visibility)
    }, [columnsState]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        filters.filter(x => x.defaultValues).map(x => ({id: x.filteringColumn, value: x.defaultValues!}))
    )
    const [sorting, setSorting] = React.useState<SortingState>(
        // @ts-ignore
        columnsState.filter(x => x.sort !== undefined).map(x => ({id: x.id, desc: x.sort === "desc"}))
    )
    const table = useReactTable({
        data,
        columns: columnsState,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    })

    useEffect(() => {
        if (table.getState().pagination.pageSize !== 10 && table.getState().pagination.pageSize !== 100 && table.getState().pagination.pageSize !== defaultPageSize || table.getState().pagination.pageIndex !== 0) {
            return;
        }
        // Set page size based on defaultPageSize prop or container height
        if (defaultPageSize) {
            table.setPageSize(defaultPageSize);
        } else if (containerHeight < 250) {
            table.setPageSize(100);
        } else {
            table.setPageSize(10);
        }
    }, [containerHeight, table, defaultPageSize]);

    const textFilteringColumnId = textFilteringColumnName || columnConfigs?.find((config: ColumnConfig) => config.isFilterable)?.id;
    let textFilteringColumnPlaceholder = textFilteringPlaceholder;
    if (textFilteringColumnId && textFilteringPlaceholder === undefined) {
        // @ts-ignore
        textFilteringColumnPlaceholder = columnsState.find(column => column.id === textFilteringColumnId)?.meta?.title || textFilteringColumnId;
        textFilteringColumnPlaceholder = `Filter by ${textFilteringColumnPlaceholder}`;
    }


    return (
        <div ref={containerRef} className={cn(
            "h-full flex flex-col space-y-0",
            !removeBorder && "border rounded",
            "bg-backgroundmedium text-textmedium",
            className
        )}>
            {(tableTitle || setTitle || textFilteringColumnId || filters.length > 0 || isEditable) && (
                <div className={"py-2 px-2 flex space-x-2 items-center flex-none"}>
                    {tableTitle && !setTitle &&
                        <div className={"font-semibold text-lg flex grow shrink"}>{tableTitle}</div>}
                    {setTitle && <div className={"font-semibold text-lg flex grow shrink"}>
                        <Input
                            placeholder={"Table Title"}
                            value={tableTitle}
                            onChange={(e) => {
                                setTitle(e.target.value)
                            }}/>
                    </div>}
                    <div className={"flex items-center space-x-2 ml-auto"}>
                        <DataTableToolbar table={table} textFilteringColumnName={textFilteringColumnId}
                                        textFilteringPlaceholder={textFilteringColumnPlaceholder} filters={filters}/>
                        <div className={"flex justify-items-end"}>
                            <DataTableViewOptions isEditable={isEditable} columnConfigs={columnConfigs}
                                                setColumnConfigs={setColumnConfigs}
                                                table={table}/>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex-1 overflow-auto min-h-0 min-w-0">
                <div className="min-w-full inline-block align-middle">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader className={cn(
                                "bg-backgrounddark rounded sticky top-0",
                                (tableTitle || setTitle || textFilteringColumnId || filters.length > 0 || isEditable) ? "border-t" : ""
                            )}>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <TableHead
                                                    className={"px-4"}
                                                    key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            )
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-full text-center"
                                        >
                                            <div className="relative top-1/2 left-1/2">
                                                <LoadingSpinner/>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && "selected"}
                                            onClick={() => onRowClick && onRowClick(row.original)}
                                            className={onRowClick ? "cursor-pointer hover:bg-backgroundlight" : ""}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell className={"px-4 break-all whitespace-pre-wrap"}
                                                           key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center"
                                        >
                                            {emptyStateMessage || "No results found"}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>
            {(forcePagination || (showPagination && (containerHeight >= 250 || data.length >= 10))) &&
                <div className={"pb-2 px-2 border-t"}>
                    <DataTablePagination table={table} containerWidth={containerWidth}/>
                </div>
            }
        </div>)
}
